import { PTAC_UNIT_GRID_SQUARES } from '@alpinehomeair/common';
import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  CondenserPlacementType,
  CondenserType,
  GridObjectType,
  FootprintPolygonsType,
  GridSpaceDataType,
  LevelType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import {
  equipmentRotationContext,
  withGridObjectRotation
} from 'utils/equipment-rotation';
import { getExistingEquipmentGridObjects } from 'utils/existing-equipment';
import {
  GridObjectsOptions,
  OverlayObjectsOptions
} from 'utils/grid-objects-types';
import { isPointInPolygon } from 'utils/footprint';
import {
  hasAnyFootprintPolygon,
  isRectInAnyPolygon
} from 'utils/footprint-polygons';
import {
  gridSpaceSnapPolygon,
  gridSpaceSnapPolygons,
  objectCenterFeet,
  objectTopLeftFeet
} from 'utils/grid-snap-polygons';
import {
  getSpaceAbsoluteVertices,
  getSpaceInteriorCenter,
  isPolygonShapedSpace
} from 'utils/point-clamp';
import {
  getSectionLabelGroupForSpace,
  getSectionLabelGroups
} from 'utils/section-labels';
import {
  boundingBoxOfVertices,
  getSpaceVertices,
  isPolygonGridSpace,
  scaleVertices
} from 'utils/space-polygon';
import { findClosestSpaceInSection, findSpaceById } from 'utils/structure';
import {
  isSquareInsideAnyPolygon,
  moveSquareOutsidePolygons,
  snapInsidePolygon,
  snapOutsidePolygons
} from 'utils/wall-snap';
import { WallSnapPlacementType } from 'utils/wall-snap-types';

function spaceToGridRenderData(space: {
  vertices?: PositionType[];
  width?: number;
  length?: number;
  position?: { x: number; y: number };
}): {
  position: PositionAndDimensionsType;
  vertices: PositionType[];
  naturalWidth: number;
  naturalHeight: number;
} {
  const relativeVertices = getSpaceVertices(space);
  const pixelVertices = scaleVertices(relativeVertices, STRUCTURE_GRID_SIZE);
  const bb = boundingBoxOfVertices(pixelVertices);
  const originX = Math.round((space.position?.x ?? 0) * STRUCTURE_GRID_SIZE);
  const originY = Math.round((space.position?.y ?? 0) * STRUCTURE_GRID_SIZE);
  const naturalWidth = (space.width ?? 0) * STRUCTURE_GRID_SIZE;
  const naturalHeight = (space.length ?? 0) * STRUCTURE_GRID_SIZE;
  return {
    position: {
      x: originX + bb.minX,
      y: originY + bb.minY,
      width: bb.width,
      height: bb.height
    },
    vertices: pixelVertices.map((v) => ({
      x: v.x - bb.minX,
      y: v.y - bb.minY
    })),
    naturalWidth,
    naturalHeight
  };
}

export type EdgeType = 'top' | 'right' | 'bottom' | 'left';

const AIR_HANDLER_TYPE_LABELS: Record<AirHandlerTypeCodeType, string> = {
  wall: 'HW',
  cassette: 'CC',
  heatingConsole: 'LW',
  console: 'FC',
  concealed: 'CD',
  wallDIY: 'DIY',
  cassette_16: 'CC',
  cassette_drop_ceiling: 'CC',
  ducted: 'D',
  concealed_high_static: 'HS',
  evap_coil: 'EC'
};

export type SectionColorMap = Map<
  string,
  { bg: string; border: string; text: string }
>;

export function findLevelForSpace(
  structureItemId: string,
  structure: LevelType[]
): LevelType | null {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === structureItemId) {
          return level;
        }
      }
    }
  }
  return null;
}

export function findLevelForSection(
  sectionId: string,
  structure: LevelType[]
): LevelType | null {
  for (const level of structure) {
    if (level.sections.some((s) => s.uniqueId === sectionId)) {
      return level;
    }
  }
  return null;
}

export function getGridObjectsForLevel(
  levels: LevelType[],
  levelIndex: number,
  {
    configurations,
    electricalPanelsOnly = false,
    activeConfigIndex,
    concealedDuctLayouts,
    existingSystems,
    ptacDesign = false
  }: GridObjectsOptions = {}
): GridObjectType[] {
  const level = levels[levelIndex];
  if (!level) return [];

  const objects: GridObjectType[] = [];

  level.electricalPanels
    .filter((panel) => panel.position)
    .forEach((panel) => {
      objects.push({
        id: panel.uniqueId,
        itemId: panel.uniqueId,
        position: {
          x: panel.position!.x,
          y: panel.position!.y,
          width: 4,
          height: 4
        },
        type: 'electrical-panel'
      });
    });

  if (existingSystems) {
    objects.push(
      ...getExistingEquipmentGridObjects(existingSystems, level.uniqueId)
    );
  }

  const rotationContext = equipmentRotationContext(level);

  if (electricalPanelsOnly) {
    return withGridObjectRotation(objects, rotationContext);
  }

  if (configurations) {
    const configIndex = activeConfigIndex ?? 0;
    const activeConfig = configurations[configIndex];
    if (activeConfig && level) {
      const visibleCondensers = activeConfig.condensers.filter(
        (c) => c.position !== undefined
      );

      activeConfig.condensers
        .filter((condenser) => condenser.levelUniqueId === level.uniqueId)
        .forEach((condenser) => {
          if (condenser.position) {
            const condenserIndex =
              visibleCondensers.findIndex(
                (c) => c.uniqueId === condenser.uniqueId
              ) + 1;

            objects.push({
              id: condenser.uniqueId,
              itemId: condenser.uniqueId,
              position: {
                x: condenser.position.x,
                y: condenser.position.y,
                width: 4,
                height: 4
              },
              type: 'condenser',
              label: ptacDesign ? `PT${condenserIndex}` : `C${condenserIndex}`,
              heightOffGround: condenser.heightOffGround,
              levelUniqueId: condenser.levelUniqueId,
              placementType: condenser.placementType,
              ...(ptacDesign
                ? {
                    gridSquares: PTAC_UNIT_GRID_SQUARES,
                    tooltip: 'PTAC unit - through the wall'
                  }
                : {})
            });
          }
        });

      activeConfig.condensers.forEach((condenser) => {
        condenser.zones.forEach((zone) => {
          const { zoneItems, airHandler } = zone;
          if (zoneItems.length === 0 || !airHandler) return;

          const ductLayout = concealedDuctLayouts?.get(zone.uniqueId);
          const ahGridSquares = 3;

          if (ductLayout) {
            const layoutLevel = levels[ductLayout.originLevelIndex];
            if (layoutLevel?.uniqueId !== level.uniqueId) return;

            const airHandlerLabel =
              AIR_HANDLER_TYPE_LABELS[airHandler.airHandlerTypeCode] || 'AH';

            const originGridX = ductLayout.originPx.x / STRUCTURE_GRID_SIZE;
            const originGridY = ductLayout.originPx.y / STRUCTURE_GRID_SIZE;
            const halfSquares = ahGridSquares / 2;

            objects.push({
              id: `${zoneItems[0].uniqueId}-air-handler`,
              itemId: airHandler.uniqueId,
              position: {
                x: originGridX - halfSquares,
                y: originGridY - halfSquares,
                width: 4,
                height: 4
              },
              type: 'air-handler',
              label: airHandlerLabel,
              airHandlerTypeCode: airHandler.airHandlerTypeCode,
              levelUniqueId: layoutLevel.uniqueId,
              concealedLocation: ductLayout.placement ?? null
            });
            return;
          }

          const airHandlerLevel = findLevelForSpace(
            zoneItems[0].structureItemId,
            levels
          );
          if (airHandlerLevel?.uniqueId !== level.uniqueId) return;

          const airHandlerLabel =
            AIR_HANDLER_TYPE_LABELS[airHandler.airHandlerTypeCode] || 'AH';

          objects.push({
            id: `${zoneItems[0].uniqueId}-air-handler`,
            itemId: airHandler.uniqueId,
            position: {
              x: airHandler.positionX,
              y: airHandler.positionY,
              width: 4,
              height: 4
            },
            type: 'air-handler',
            label: airHandlerLabel,
            airHandlerTypeCode: airHandler.airHandlerTypeCode,
            levelUniqueId: airHandlerLevel.uniqueId
          });
        });
      });
    }
  }

  return withGridObjectRotation(objects, rotationContext);
}

export type AdjacentSpaces = {
  top: GridSpaceDataType | undefined;
  right: GridSpaceDataType | undefined;
  bottom: GridSpaceDataType | undefined;
  left: GridSpaceDataType | undefined;
};

export type AllAdjacentSpaces = {
  top: GridSpaceDataType[];
  right: GridSpaceDataType[];
  bottom: GridSpaceDataType[];
  left: GridSpaceDataType[];
};

export function getAdjacentSpaces(
  space: GridSpaceDataType,
  allSpaces: GridSpaceDataType[]
): AdjacentSpaces {
  const currentPos = space.position;

  return {
    top: allSpaces.find((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.x < otherPos.x + otherPos.width &&
        currentPos.x + currentPos.width > otherPos.x &&
        currentPos.y === otherPos.y + otherPos.height
      );
    }),
    right: allSpaces.find((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.y < otherPos.y + otherPos.height &&
        currentPos.y + currentPos.height > otherPos.y &&
        currentPos.x + currentPos.width === otherPos.x
      );
    }),
    bottom: allSpaces.find((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.x < otherPos.x + otherPos.width &&
        currentPos.x + currentPos.width > otherPos.x &&
        currentPos.y + currentPos.height === otherPos.y
      );
    }),
    left: allSpaces.find((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.y < otherPos.y + otherPos.height &&
        currentPos.y + currentPos.height > otherPos.y &&
        currentPos.x === otherPos.x + otherPos.width
      );
    })
  };
}

export function getAllAdjacentSpaces(
  space: GridSpaceDataType,
  allSpaces: GridSpaceDataType[]
): AllAdjacentSpaces {
  const currentPos = space.position;

  return {
    top: allSpaces.filter((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.x < otherPos.x + otherPos.width &&
        currentPos.x + currentPos.width > otherPos.x &&
        currentPos.y === otherPos.y + otherPos.height
      );
    }),
    right: allSpaces.filter((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.y < otherPos.y + otherPos.height &&
        currentPos.y + currentPos.height > otherPos.y &&
        currentPos.x + currentPos.width === otherPos.x
      );
    }),
    bottom: allSpaces.filter((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.x < otherPos.x + otherPos.width &&
        currentPos.x + currentPos.width > otherPos.x &&
        currentPos.y + currentPos.height === otherPos.y
      );
    }),
    left: allSpaces.filter((other) => {
      if (other.id === space.id) return false;
      const otherPos = other.position;
      return (
        currentPos.y < otherPos.y + otherPos.height &&
        currentPos.y + currentPos.height > otherPos.y &&
        currentPos.x === otherPos.x + otherPos.width
      );
    })
  };
}

export function getBorderRadiusClass(
  space: GridSpaceDataType,
  allSpaces: GridSpaceDataType[]
) {
  const isCornerAtIntersection = (corner: 'tl' | 'tr' | 'bl' | 'br') => {
    const currentPos = space.position;
    const cornerPositions = {
      tl: { x: currentPos.x, y: currentPos.y },
      tr: { x: currentPos.x + currentPos.width, y: currentPos.y },
      bl: { x: currentPos.x, y: currentPos.y + currentPos.height },
      br: {
        x: currentPos.x + currentPos.width,
        y: currentPos.y + currentPos.height
      }
    };

    const cornerPos = cornerPositions[corner];

    return allSpaces.some((otherSpace) => {
      if (otherSpace.id === space.id) return false;
      const otherPos = otherSpace.position;
      return (
        cornerPos.x >= otherPos.x &&
        cornerPos.x <= otherPos.x + otherPos.width &&
        cornerPos.y >= otherPos.y &&
        cornerPos.y <= otherPos.y + otherPos.height
      );
    });
  };

  const corners: string[] = [];
  if (!isCornerAtIntersection('tl')) corners.push('rounded-tl');
  if (!isCornerAtIntersection('tr')) corners.push('rounded-tr');
  if (!isCornerAtIntersection('bl')) corners.push('rounded-bl');
  if (!isCornerAtIntersection('br')) corners.push('rounded-br');

  return corners.join(' ');
}

export function getOpenToBorderRadiusClass(
  space: GridSpaceDataType,
  allSpaces: GridSpaceDataType[],
  structure: LevelType[]
) {
  const currentSpace = findSpaceById(structure, space.spaceId);
  const currentPos = space.position;

  const isCornerAtIntersection = (corner: 'tl' | 'tr' | 'bl' | 'br') => {
    const cornerPositions = {
      tl: { x: currentPos.x, y: currentPos.y },
      tr: { x: currentPos.x + currentPos.width, y: currentPos.y },
      bl: { x: currentPos.x, y: currentPos.y + currentPos.height },
      br: {
        x: currentPos.x + currentPos.width,
        y: currentPos.y + currentPos.height
      }
    };

    const cornerPos = cornerPositions[corner];

    return allSpaces.some((otherSpace) => {
      if (otherSpace.id === space.id) {
        return false;
      }

      if (!currentSpace?.openTo?.includes(otherSpace.spaceId)) {
        return false;
      }

      const otherPos = otherSpace.position;
      return (
        cornerPos.x >= otherPos.x &&
        cornerPos.x <= otherPos.x + otherPos.width &&
        cornerPos.y >= otherPos.y &&
        cornerPos.y <= otherPos.y + otherPos.height
      );
    });
  };

  const corners: string[] = [];
  if (!isCornerAtIntersection('tl')) corners.push('rounded-tl');
  if (!isCornerAtIntersection('tr')) corners.push('rounded-tr');
  if (!isCornerAtIntersection('bl')) corners.push('rounded-bl');
  if (!isCornerAtIntersection('br')) corners.push('rounded-br');

  return corners.join(' ');
}

export function getShouldShowLabel(
  space: GridSpaceDataType,
  allSpaces: GridSpaceDataType[],
  structure: LevelType[],
  hasOpenTo: boolean
) {
  if (!hasOpenTo || allSpaces.length <= 1) {
    return true;
  }

  const groups = getSectionLabelGroups(allSpaces, structure);
  const group = getSectionLabelGroupForSpace(space, groups);
  if (!group) {
    return true;
  }

  return group.largestSpace.id === space.id;
}

export function isTouchingSpace(
  currentPos: PositionAndDimensionsType,
  otherPos: PositionAndDimensionsType
): boolean {
  if (
    currentPos.x < otherPos.x + otherPos.width &&
    currentPos.x + currentPos.width > otherPos.x
  ) {
    if (
      currentPos.y === otherPos.y + otherPos.height ||
      currentPos.y + currentPos.height === otherPos.y
    ) {
      return true;
    }
  }

  if (
    currentPos.y < otherPos.y + otherPos.height &&
    currentPos.y + currentPos.height > otherPos.y
  ) {
    if (
      currentPos.x === otherPos.x + otherPos.width ||
      currentPos.x + currentPos.width === otherPos.x
    ) {
      return true;
    }
  }

  return false;
}

export function adjustPositionToTouch(
  currentPos: PositionAndDimensionsType,
  targetPos: PositionAndDimensionsType
): PositionAndDimensionsType {
  const currentCenterX = currentPos.x + currentPos.width / 2;
  const currentCenterY = currentPos.y + currentPos.height / 2;
  const targetCenterX = targetPos.x + targetPos.width / 2;
  const targetCenterY = targetPos.y + targetPos.height / 2;

  const deltaX = currentCenterX - targetCenterX;
  const deltaY = currentCenterY - targetCenterY;

  const adjustedPos = { ...currentPos };

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    if (deltaX > 0) {
      adjustedPos.x = Math.round(targetPos.x + targetPos.width);
    } else {
      adjustedPos.x = Math.round(targetPos.x - currentPos.width);
    }
  } else if (deltaY > 0) {
    adjustedPos.y = Math.round(targetPos.y + targetPos.height);
  } else {
    adjustedPos.y = Math.round(targetPos.y - currentPos.height);
  }

  return adjustedPos;
}

export type ResizeParams = {
  gridSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  newPosition: PositionAndDimensionsType;
};

export function calculateResizedPosition({
  gridSpace,
  allGridSpaces,
  newPosition
}: ResizeParams): PositionAndDimensionsType {
  let finalPosition = newPosition;

  if (
    gridSpace.sectionId &&
    gridSpace.spacesInSection &&
    gridSpace.spacesInSection > 1
  ) {
    const sameSectionSpaces = allGridSpaces.filter(
      (s) => s.sectionId === gridSpace.sectionId && s.id !== gridSpace.id
    );

    if (sameSectionSpaces.length > 0) {
      const isTouchingAny = sameSectionSpaces.some((otherSpace) => {
        const otherPosInFeet = {
          x: otherSpace.position.x / STRUCTURE_GRID_SIZE,
          y: otherSpace.position.y / STRUCTURE_GRID_SIZE,
          width: otherSpace.position.width / STRUCTURE_GRID_SIZE,
          height: otherSpace.position.height / STRUCTURE_GRID_SIZE
        };
        return isTouchingSpace(newPosition, otherPosInFeet);
      });

      if (!isTouchingAny) {
        const otherSpacesForClosest = sameSectionSpaces.map((s) => ({
          id: s.id,
          position: {
            x: s.position.x / STRUCTURE_GRID_SIZE,
            y: s.position.y / STRUCTURE_GRID_SIZE,
            width: s.position.width / STRUCTURE_GRID_SIZE,
            height: s.position.height / STRUCTURE_GRID_SIZE
          }
        }));

        const closestSpace = findClosestSpaceInSection(
          newPosition,
          otherSpacesForClosest
        );

        if (closestSpace) {
          finalPosition = adjustPositionToTouch(
            newPosition,
            closestSpace.position
          );
        }
      }
    }
  }

  return finalPosition;
}

export type CornerResizeParams = {
  handle: 'nw' | 'ne' | 'sw' | 'se';
  deltaX: number;
  deltaY: number;
  initialPosition: PositionAndDimensionsType;
  currentPosition: PositionAndDimensionsType;
  snapToGrid: (value: number) => number;
};

export function calculateCornerResizePosition({
  handle,
  deltaX,
  deltaY,
  initialPosition,
  currentPosition,
  snapToGrid
}: CornerResizeParams): PositionAndDimensionsType {
  const newPosition = {
    x: currentPosition.x / STRUCTURE_GRID_SIZE,
    y: currentPosition.y / STRUCTURE_GRID_SIZE,
    width: currentPosition.width / STRUCTURE_GRID_SIZE,
    height: currentPosition.height / STRUCTURE_GRID_SIZE
  };

  const initialRight = snapToGrid(initialPosition.x + initialPosition.width);
  const initialBottom = snapToGrid(initialPosition.y + initialPosition.height);

  switch (handle) {
    case 'nw':
      newPosition.x = snapToGrid(initialPosition.x + deltaX);
      newPosition.y = snapToGrid(initialPosition.y + deltaY);
      newPosition.width = initialRight - newPosition.x;
      newPosition.height = initialBottom - newPosition.y;
      break;
    case 'ne':
      newPosition.y = snapToGrid(initialPosition.y + deltaY);
      newPosition.width = snapToGrid(initialPosition.width + deltaX);
      newPosition.height = initialBottom - newPosition.y;
      break;
    case 'sw':
      newPosition.x = snapToGrid(initialPosition.x + deltaX);
      newPosition.width = initialRight - newPosition.x;
      newPosition.height = snapToGrid(initialPosition.height + deltaY);
      break;
    case 'se':
      newPosition.width = snapToGrid(initialPosition.width + deltaX);
      newPosition.height = snapToGrid(initialPosition.height + deltaY);
      break;
    default:
      break;
  }

  newPosition.width = Math.max(
    MIN_SPACE_DIM_FEET,
    Math.round(newPosition.width)
  );
  newPosition.height = Math.max(
    MIN_SPACE_DIM_FEET,
    Math.round(newPosition.height)
  );

  return newPosition;
}

export type EdgeResizeParams = {
  edge: 'top' | 'right' | 'bottom' | 'left';
  deltaX: number;
  deltaY: number;
  initialPosition: PositionAndDimensionsType;
  currentPosition: PositionAndDimensionsType;
  snapToGrid: (value: number) => number;
};

export function calculateEdgeResizePosition({
  edge,
  deltaX,
  deltaY,
  initialPosition,
  currentPosition,
  snapToGrid
}: EdgeResizeParams): PositionAndDimensionsType {
  const newPosition = {
    x: currentPosition.x / STRUCTURE_GRID_SIZE,
    y: currentPosition.y / STRUCTURE_GRID_SIZE,
    width: currentPosition.width / STRUCTURE_GRID_SIZE,
    height: currentPosition.height / STRUCTURE_GRID_SIZE
  };

  const initialRight = snapToGrid(initialPosition.x + initialPosition.width);
  const initialBottom = snapToGrid(initialPosition.y + initialPosition.height);

  switch (edge) {
    case 'top':
      newPosition.y = snapToGrid(initialPosition.y + deltaY);
      newPosition.height = initialBottom - newPosition.y;
      break;
    case 'right':
      newPosition.width = snapToGrid(initialPosition.width + deltaX);
      break;
    case 'bottom':
      newPosition.height = snapToGrid(initialPosition.height + deltaY);
      break;
    case 'left':
      newPosition.x = snapToGrid(initialPosition.x + deltaX);
      newPosition.width = initialRight - newPosition.x;
      break;
    default:
      break;
  }

  newPosition.width = Math.max(
    MIN_SPACE_DIM_FEET,
    Math.round(newPosition.width)
  );
  newPosition.height = Math.max(
    MIN_SPACE_DIM_FEET,
    Math.round(newPosition.height)
  );

  return newPosition;
}

type SharedEdgeResizeParams = {
  activeSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  requestedActivePosition: PositionAndDimensionsType;
  edge: EdgeType;
  minSize?: number;
  footprintPolygons?: FootprintPolygonsType | null;
};

type TouchingSharedEdgeSpace = {
  spaceId: string;
  normalizedPosition: PositionAndDimensionsType;
};

function rangesOverlap(
  startA: number,
  endA: number,
  startB: number,
  endB: number
): boolean {
  return startA < endB && endA > startB;
}

function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getSharedEdgeCoordinate(
  position: PositionAndDimensionsType,
  edge: EdgeType
): number {
  if (edge === 'top') return position.y;
  if (edge === 'right') return position.x + position.width;
  if (edge === 'bottom') return position.y + position.height;
  return position.x;
}

function normalizeSpacePosition(
  position: PositionAndDimensionsType
): PositionAndDimensionsType {
  return {
    x: position.x / STRUCTURE_GRID_SIZE,
    y: position.y / STRUCTURE_GRID_SIZE,
    width: position.width / STRUCTURE_GRID_SIZE,
    height: position.height / STRUCTURE_GRID_SIZE
  };
}

function buildSharedEdgeResizePositions(
  edge: EdgeType,
  sharedEdge: number,
  activeSpaceId: string,
  activeInitialPosition: PositionAndDimensionsType,
  touchingSpaces: TouchingSharedEdgeSpace[]
): Map<string, PositionAndDimensionsType> {
  const activeLeft = activeInitialPosition.x;
  const activeTop = activeInitialPosition.y;
  const activeRight = activeInitialPosition.x + activeInitialPosition.width;
  const activeBottom = activeInitialPosition.y + activeInitialPosition.height;

  const result = new Map<string, PositionAndDimensionsType>();

  if (edge === 'top') {
    result.set(activeSpaceId, {
      x: activeLeft,
      y: sharedEdge,
      width: activeInitialPosition.width,
      height: activeBottom - sharedEdge
    });
  } else if (edge === 'bottom') {
    result.set(activeSpaceId, {
      x: activeLeft,
      y: activeTop,
      width: activeInitialPosition.width,
      height: sharedEdge - activeTop
    });
  } else if (edge === 'left') {
    result.set(activeSpaceId, {
      x: sharedEdge,
      y: activeTop,
      width: activeRight - sharedEdge,
      height: activeInitialPosition.height
    });
  } else {
    result.set(activeSpaceId, {
      x: activeLeft,
      y: activeTop,
      width: sharedEdge - activeLeft,
      height: activeInitialPosition.height
    });
  }

  for (const touchingSpace of touchingSpaces) {
    const other = touchingSpace.normalizedPosition;
    const otherBottom = other.y + other.height;
    const otherRight = other.x + other.width;

    if (edge === 'top') {
      result.set(touchingSpace.spaceId, {
        x: other.x,
        y: other.y,
        width: other.width,
        height: sharedEdge - other.y
      });
    } else if (edge === 'bottom') {
      result.set(touchingSpace.spaceId, {
        x: other.x,
        y: sharedEdge,
        width: other.width,
        height: otherBottom - sharedEdge
      });
    } else if (edge === 'left') {
      result.set(touchingSpace.spaceId, {
        x: other.x,
        y: other.y,
        width: sharedEdge - other.x,
        height: other.height
      });
    } else {
      result.set(touchingSpace.spaceId, {
        x: sharedEdge,
        y: other.y,
        width: otherRight - sharedEdge,
        height: other.height
      });
    }
  }

  return result;
}

function constrainSharedEdgeToFootprint(
  edge: EdgeType,
  proposedSharedEdge: number,
  initialSharedEdge: number,
  activeSpaceId: string,
  activeInitialPosition: PositionAndDimensionsType,
  touchingSpaces: TouchingSharedEdgeSpace[],
  footprintPolygons: FootprintPolygonsType
): number {
  const allPositionsInsideFootprint = (sharedEdge: number): boolean => {
    const positions = buildSharedEdgeResizePositions(
      edge,
      sharedEdge,
      activeSpaceId,
      activeInitialPosition,
      touchingSpaces
    );
    for (const position of positions.values()) {
      if (!isRectInAnyPolygon(position, footprintPolygons)) {
        return false;
      }
    }
    return true;
  };

  if (!allPositionsInsideFootprint(initialSharedEdge)) {
    return proposedSharedEdge;
  }

  if (proposedSharedEdge === initialSharedEdge) {
    return proposedSharedEdge;
  }

  const direction = proposedSharedEdge > initialSharedEdge ? -1 : 1;
  const maxIterations =
    Math.ceil(Math.abs(proposedSharedEdge - initialSharedEdge)) + 2;
  let candidate = proposedSharedEdge;

  for (let i = 0; i < maxIterations; i += 1) {
    if (allPositionsInsideFootprint(candidate)) {
      return candidate;
    }
    candidate += direction;
    if (
      (direction === -1 && candidate <= initialSharedEdge) ||
      (direction === 1 && candidate >= initialSharedEdge)
    ) {
      return initialSharedEdge;
    }
  }

  return initialSharedEdge;
}

function isSharedEdgeTouching(
  edge: EdgeType,
  other: PositionAndDimensionsType,
  active: { top: number; bottom: number; left: number; right: number }
): boolean {
  const otherTop = other.y;
  const otherBottom = other.y + other.height;
  const otherLeft = other.x;
  const otherRight = other.x + other.width;

  if (edge === 'top') {
    return (
      otherBottom === active.top &&
      rangesOverlap(otherLeft, otherRight, active.left, active.right)
    );
  }
  if (edge === 'bottom') {
    return (
      otherTop === active.bottom &&
      rangesOverlap(otherLeft, otherRight, active.left, active.right)
    );
  }
  if (edge === 'left') {
    return (
      otherRight === active.left &&
      rangesOverlap(otherTop, otherBottom, active.top, active.bottom)
    );
  }
  return (
    otherLeft === active.right &&
    rangesOverlap(otherTop, otherBottom, active.top, active.bottom)
  );
}

function tightenBoundsForActiveGrowth(
  edge: EdgeType,
  active: { top: number; bottom: number; left: number; right: number },
  obstacle: PositionAndDimensionsType,
  bounds: { lowerBound: number; upperBound: number }
): { lowerBound: number; upperBound: number } {
  const oTop = obstacle.y;
  const oBottom = obstacle.y + obstacle.height;
  const oLeft = obstacle.x;
  const oRight = obstacle.x + obstacle.width;

  if (edge === 'right') {
    if (
      rangesOverlap(oTop, oBottom, active.top, active.bottom) &&
      oLeft >= active.right
    ) {
      return { ...bounds, upperBound: Math.min(bounds.upperBound, oLeft) };
    }
  } else if (edge === 'left') {
    if (
      rangesOverlap(oTop, oBottom, active.top, active.bottom) &&
      oRight <= active.left
    ) {
      return { ...bounds, lowerBound: Math.max(bounds.lowerBound, oRight) };
    }
  } else if (edge === 'top') {
    if (
      rangesOverlap(oLeft, oRight, active.left, active.right) &&
      oBottom <= active.top
    ) {
      return { ...bounds, lowerBound: Math.max(bounds.lowerBound, oBottom) };
    }
  } else if (
    rangesOverlap(oLeft, oRight, active.left, active.right) &&
    oTop >= active.bottom
  ) {
    return { ...bounds, upperBound: Math.min(bounds.upperBound, oTop) };
  }

  return bounds;
}

function tightenBoundsForNeighborGrowth(
  edge: EdgeType,
  neighbor: PositionAndDimensionsType,
  obstacle: PositionAndDimensionsType,
  bounds: { lowerBound: number; upperBound: number }
): { lowerBound: number; upperBound: number } {
  const nTop = neighbor.y;
  const nBottom = neighbor.y + neighbor.height;
  const nLeft = neighbor.x;
  const nRight = neighbor.x + neighbor.width;
  const oTop = obstacle.y;
  const oBottom = obstacle.y + obstacle.height;
  const oLeft = obstacle.x;
  const oRight = obstacle.x + obstacle.width;

  if (edge === 'right') {
    if (rangesOverlap(oTop, oBottom, nTop, nBottom) && oRight <= nLeft) {
      return { ...bounds, lowerBound: Math.max(bounds.lowerBound, oRight) };
    }
  } else if (edge === 'left') {
    if (rangesOverlap(oTop, oBottom, nTop, nBottom) && oLeft >= nRight) {
      return { ...bounds, upperBound: Math.min(bounds.upperBound, oLeft) };
    }
  } else if (edge === 'top') {
    if (rangesOverlap(oLeft, oRight, nLeft, nRight) && oTop >= nBottom) {
      return { ...bounds, upperBound: Math.min(bounds.upperBound, oTop) };
    }
  } else if (rangesOverlap(oLeft, oRight, nLeft, nRight) && oBottom <= nTop) {
    return { ...bounds, lowerBound: Math.max(bounds.lowerBound, oBottom) };
  }

  return bounds;
}

export function calculateSharedEdgeResizePositions({
  activeSpace,
  allGridSpaces,
  requestedActivePosition,
  edge,
  minSize = MIN_SPACE_DIM_FEET,
  footprintPolygons
}: SharedEdgeResizeParams): Map<string, PositionAndDimensionsType> | null {
  const activeInitialPosition = normalizeSpacePosition(activeSpace.position);
  const active = {
    top: activeInitialPosition.y,
    bottom: activeInitialPosition.y + activeInitialPosition.height,
    left: activeInitialPosition.x,
    right: activeInitialPosition.x + activeInitialPosition.width
  };

  const touchingSpaces: TouchingSharedEdgeSpace[] = [];
  const obstacleRects: PositionAndDimensionsType[] = [];

  const otherSpaces = allGridSpaces.filter((s) => s.id !== activeSpace.id);
  for (const space of otherSpaces) {
    const normalized = normalizeSpacePosition(space.position);
    const isPolygon = isPolygonGridSpace(
      space.vertices,
      space.position.width,
      space.position.height
    );

    if (!isPolygon && isSharedEdgeTouching(edge, normalized, active)) {
      touchingSpaces.push({
        spaceId: space.spaceId,
        normalizedPosition: normalized
      });
    } else {
      obstacleRects.push(normalized);
    }
  }

  if (touchingSpaces.length === 0) {
    return null;
  }

  const proposedSharedEdge = getSharedEdgeCoordinate(
    requestedActivePosition,
    edge
  );

  let bounds = { lowerBound: -Infinity, upperBound: Infinity };

  if (edge === 'top') {
    bounds.upperBound = active.bottom - minSize;
  } else if (edge === 'bottom') {
    bounds.lowerBound = active.top + minSize;
  } else if (edge === 'left') {
    bounds.upperBound = active.right - minSize;
  } else {
    bounds.lowerBound = active.left + minSize;
  }

  for (const touchingSpace of touchingSpaces) {
    const other = touchingSpace.normalizedPosition;
    const otherBottom = other.y + other.height;
    const otherRight = other.x + other.width;

    if (edge === 'top') {
      bounds.lowerBound = Math.max(bounds.lowerBound, other.y + minSize);
    } else if (edge === 'bottom') {
      bounds.upperBound = Math.min(bounds.upperBound, otherBottom - minSize);
    } else if (edge === 'left') {
      bounds.lowerBound = Math.max(bounds.lowerBound, other.x + minSize);
    } else {
      bounds.upperBound = Math.min(bounds.upperBound, otherRight - minSize);
    }
  }

  for (const obstacle of obstacleRects) {
    bounds = tightenBoundsForActiveGrowth(edge, active, obstacle, bounds);
    for (const touchingSpace of touchingSpaces) {
      bounds = tightenBoundsForNeighborGrowth(
        edge,
        touchingSpace.normalizedPosition,
        obstacle,
        bounds
      );
    }
  }

  if (bounds.lowerBound > bounds.upperBound) {
    return null;
  }

  let constrainedSharedEdge = clampValue(
    proposedSharedEdge,
    bounds.lowerBound,
    bounds.upperBound
  );

  if (hasAnyFootprintPolygon(footprintPolygons)) {
    const initialSharedEdge = getSharedEdgeCoordinate(
      activeInitialPosition,
      edge
    );
    constrainedSharedEdge = constrainSharedEdgeToFootprint(
      edge,
      constrainedSharedEdge,
      initialSharedEdge,
      activeSpace.spaceId,
      activeInitialPosition,
      touchingSpaces,
      footprintPolygons ?? []
    );
  }

  return buildSharedEdgeResizePositions(
    edge,
    constrainedSharedEdge,
    activeSpace.spaceId,
    activeInitialPosition,
    touchingSpaces
  );
}

export type ResizeDirection =
  | 'nw'
  | 'ne'
  | 'sw'
  | 'se'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

/**
 * Constrains a resize position so its free (moving) edges do not cross into
 * any neighbouring space. Anchored edges are left unchanged; only the
 * dimensions / origin of the free edges are clamped.
 *
 * For corner resizes (two free axes), the axes are evaluated independently
 * and the candidate preserving the most area is chosen.
 */
export function constrainResizeToBoundaries(
  position: PositionAndDimensionsType,
  otherSpaces: PositionAndDimensionsType[],
  direction: ResizeDirection
): PositionAndDimensionsType {
  const freeRight = ['ne', 'se', 'right'].includes(direction);
  const freeBottom = ['sw', 'se', 'bottom'].includes(direction);
  const freeLeft = ['nw', 'sw', 'left'].includes(direction);
  const freeTop = ['nw', 'ne', 'top'].includes(direction);

  const rectsOverlap = (
    a: PositionAndDimensionsType,
    b: PositionAndDimensionsType
  ): boolean =>
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y;

  const hasAnyOverlap = (pos: PositionAndDimensionsType): boolean =>
    otherSpaces.some((o) => rectsOverlap(pos, o));

  const applyMin = (pos: PositionAndDimensionsType) => ({
    ...pos,
    width: Math.max(MIN_SPACE_DIM_FEET, Math.round(pos.width)),
    height: Math.max(MIN_SPACE_DIM_FEET, Math.round(pos.height))
  });

  const constrainHorizontal = (
    pos: PositionAndDimensionsType
  ): PositionAndDimensionsType => {
    const r = { ...pos };
    const anchoredRight = r.x + r.width;

    if (freeRight) {
      let maxRight = r.x + r.width;
      for (const other of otherSpaces) {
        const overlapsVertically =
          other.y < r.y + r.height && other.y + other.height > r.y;
        if (overlapsVertically && other.x > r.x && other.x < maxRight) {
          maxRight = other.x;
        }
      }
      r.width = maxRight - r.x;
    }

    if (freeLeft) {
      let minLeft = r.x;
      for (const other of otherSpaces) {
        const overlapsVertically =
          other.y < r.y + r.height && other.y + other.height > r.y;
        const otherRight = other.x + other.width;
        if (
          overlapsVertically &&
          otherRight < anchoredRight &&
          otherRight > minLeft
        ) {
          minLeft = otherRight;
        }
      }
      if (minLeft > r.x) {
        r.width = anchoredRight - minLeft;
        r.x = minLeft;
      }
    }

    return r;
  };

  const constrainVertical = (
    pos: PositionAndDimensionsType
  ): PositionAndDimensionsType => {
    const r = { ...pos };
    const anchoredBottom = r.y + r.height;

    if (freeBottom) {
      let maxBottom = r.y + r.height;
      for (const other of otherSpaces) {
        const overlapsHorizontally =
          other.x < r.x + r.width && other.x + other.width > r.x;
        if (overlapsHorizontally && other.y > r.y && other.y < maxBottom) {
          maxBottom = other.y;
        }
      }
      r.height = maxBottom - r.y;
    }

    if (freeTop) {
      let minTop = r.y;
      for (const other of otherSpaces) {
        const overlapsHorizontally =
          other.x < r.x + r.width && other.x + other.width > r.x;
        const otherBottom = other.y + other.height;
        if (
          overlapsHorizontally &&
          otherBottom < anchoredBottom &&
          otherBottom > minTop
        ) {
          minTop = otherBottom;
        }
      }
      if (minTop > r.y) {
        r.height = anchoredBottom - minTop;
        r.y = minTop;
      }
    }

    return r;
  };

  const isCorner = ['nw', 'ne', 'sw', 'se'].includes(direction);

  if (isCorner) {
    const hOnly = applyMin(constrainHorizontal({ ...position }));
    const vOnly = applyMin(constrainVertical({ ...position }));

    const hValid = !hasAnyOverlap(hOnly);
    const vValid = !hasAnyOverlap(vOnly);

    if (hValid && vValid) {
      const hArea = hOnly.width * hOnly.height;
      const vArea = vOnly.width * vOnly.height;
      return hArea >= vArea ? hOnly : vOnly;
    }
    if (hValid) return hOnly;
    if (vValid) return vOnly;

    const both = applyMin(
      constrainVertical(constrainHorizontal({ ...position }))
    );
    return both;
  }

  const result = applyMin(
    constrainVertical(constrainHorizontal({ ...position }))
  );
  return result;
}

export function isBottomLevel(
  levels: LevelType[],
  levelIndex: number
): boolean {
  if (levels.length === 0) return false;

  const levelBelow = levelIndex > 0 ? levels[levelIndex - 1] : null;

  return (
    levelIndex === 0 ||
    levelBelow?.type === 'basement' ||
    levelBelow?.type === 'crawlspace'
  );
}

export function isTopLevel(levels: LevelType[], levelIndex: number): boolean {
  if (levels.length === 0) return false;

  const levelAbove =
    levelIndex < levels.length - 1 ? levels[levelIndex + 1] : null;

  return levelIndex === levels.length - 1 || levelAbove?.type === 'attic';
}

export function isInsideAnySpace(
  position: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[]
): boolean {
  return isSquareInsideAnyPolygon(
    objectCenterFeet(position, objectSize),
    objectSize / STRUCTURE_GRID_SIZE,
    gridSpaceSnapPolygons(gridSpaces)
  );
}

export function findNearestSpace(
  position: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[]
): GridSpaceDataType | null {
  if (gridSpaces.length === 0) return null;

  const objectPixelX = position.x * STRUCTURE_GRID_SIZE;
  const objectPixelY = position.y * STRUCTURE_GRID_SIZE;
  const objectCenterX = objectPixelX + objectSize / 2;
  const objectCenterY = objectPixelY + objectSize / 2;

  let closestSpace: GridSpaceDataType | null = null;
  let minDistance = Infinity;

  for (const space of gridSpaces) {
    const spaceCenter = getSpaceInteriorCenter(space);

    const distance = Math.sqrt(
      (spaceCenter.x - objectCenterX) ** 2 +
        (spaceCenter.y - objectCenterY) ** 2
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestSpace = space;
    }
  }

  return closestSpace;
}

export function findNearestCondenser(
  spacePosition: { x: number; y: number },
  condensers: CondenserType[]
): CondenserType | null {
  const placed = condensers.filter((c) => c.position !== undefined);
  if (placed.length === 0) return null;

  let closest: CondenserType | null = null;
  let minDistance = Infinity;

  for (const condenser of placed) {
    const pos = condenser.position!;
    const dist = Math.hypot(pos.x - spacePosition.x, pos.y - spacePosition.y);
    if (dist < minDistance) {
      minDistance = dist;
      closest = condenser;
    }
  }

  return closest;
}

export function getGridSpacesForLevel(
  levels: LevelType[],
  levelIndex: number,
  sectionColors?: SectionColorMap,
  spaceColors?: SectionColorMap,
  zoneNames?: Map<string, string>
): GridSpaceDataType[] {
  const level = levels[levelIndex];
  const gridSpaces: GridSpaceDataType[] = [];

  level?.sections.flatMap((section) =>
    section.spaces.forEach((space) => {
      const colors =
        spaceColors?.get(space.uniqueId) ??
        sectionColors?.get(section.uniqueId);
      const { position, vertices, naturalWidth, naturalHeight } =
        spaceToGridRenderData(space);
      gridSpaces.push({
        id: space.uniqueId,
        spaceId: space.uniqueId,
        label: space.label,
        position,
        vertices,
        naturalWidth,
        naturalHeight,
        type: 'space',
        itemId: space.itemId,
        sectionId: section.uniqueId,
        spacesInSection: section.spaces.length,
        sectionColor: colors,
        zoneName: zoneNames?.get(space.uniqueId)
      });
    })
  );

  return gridSpaces;
}

export function getOverlaySpacesForLevel(
  levels: LevelType[],
  levelIndex: number
): GridSpaceDataType[] {
  const overlaySpaces: GridSpaceDataType[] = [];
  const onBottomLevel = isBottomLevel(levels, levelIndex);

  if (onBottomLevel) {
    const levelAboveIndex = levelIndex + 1;
    const levelAbove = levels[levelAboveIndex];

    levelAbove?.sections.flatMap((section) =>
      section.spaces.forEach((space) => {
        const { position, vertices, naturalWidth, naturalHeight } =
          spaceToGridRenderData(space);
        overlaySpaces.push({
          id: `${space.uniqueId}-overlay`,
          spaceId: space.uniqueId,
          label: space.label,
          position,
          vertices,
          naturalWidth,
          naturalHeight,
          type: 'overlay',
          itemId: space.itemId,
          sectionId: section.uniqueId,
          spacesInSection: section.spaces.length,
          overlayDirection: 'above'
        });
      })
    );
  } else {
    const levelBelowIndex = levelIndex - 1;
    const levelBelow = levels[levelBelowIndex];

    levelBelow?.sections.flatMap((section) =>
      section.spaces.forEach((space) => {
        const { position, vertices, naturalWidth, naturalHeight } =
          spaceToGridRenderData(space);
        overlaySpaces.push({
          id: `${space.uniqueId}-overlay`,
          spaceId: space.uniqueId,
          label: space.label,
          position,
          vertices,
          naturalWidth,
          naturalHeight,
          type: 'overlay',
          itemId: space.itemId,
          sectionId: section.uniqueId,
          spacesInSection: section.spaces.length,
          overlayDirection: 'below'
        });
      })
    );
  }

  return overlaySpaces;
}

export function getOverlayObjectsForLevel(
  levels: LevelType[],
  levelIndex: number,
  {
    configurations,
    activeConfigIndex,
    concealedDuctLayouts,
    ptacDesign
  }: OverlayObjectsOptions
): GridObjectType[] {
  if (!configurations) return [];

  const onBottomLevel = isBottomLevel(levels, levelIndex);
  const adjacentIndex = onBottomLevel ? levelIndex + 1 : levelIndex - 1;

  if (adjacentIndex < 0 || adjacentIndex >= levels.length) return [];

  const overlayDirection = onBottomLevel ? 'above' : 'below';

  return getGridObjectsForLevel(levels, adjacentIndex, {
    configurations,
    activeConfigIndex,
    concealedDuctLayouts,
    ptacDesign
  })
    .filter((obj) => obj.type !== 'electrical-panel')
    .map((obj) => ({
      ...obj,
      id: `${obj.id}-overlay`,
      overlay: true,
      overlayDirection
    }));
}

export function moveOutsideSpaces(
  position: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[]
): { x: number; y: number } {
  if (gridSpaces.length === 0) return position;
  const center = moveSquareOutsidePolygons(
    objectCenterFeet(position, objectSize),
    objectSize / STRUCTURE_GRID_SIZE,
    gridSpaceSnapPolygons(gridSpaces)
  );
  return objectTopLeftFeet(center, objectSize);
}

export function findNearestExternalEdge(
  position: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[],
  footprints: FootprintPolygonsType
): WallSnapPlacementType | null {
  const polygons = gridSpaceSnapPolygons(gridSpaces);
  return snapOutsidePolygons(
    objectCenterFeet(position, objectSize),
    objectSize / STRUCTURE_GRID_SIZE,
    polygons,
    { blockers: polygons, footprints }
  );
}

export function getCondenserPlacementType(
  dragType: string
): CondenserPlacementType | null {
  const typeMap: Record<string, CondenserPlacementType> = {
    'new-condenser-ground': 'ground',
    'new-condenser-wall': 'wall',
    'new-condenser-roof': 'roof'
  };
  return typeMap[dragType] || null;
}

export function determineCondenserPlacement(
  levels: LevelType[],
  selectedLevelIndex: number,
  dropPosition: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[]
): CondenserPlacementType {
  if (isBottomLevel(levels, selectedLevelIndex)) {
    return 'ground';
  }

  if (isTopLevel(levels, selectedLevelIndex)) {
    const insideSpace = isInsideAnySpace(dropPosition, objectSize, gridSpaces);
    return insideSpace ? 'roof' : 'wall';
  }

  return 'wall';
}

export type SnapToInnerEdgeOptions = {
  allGridSpaces?: GridSpaceDataType[];
  structure?: LevelType[];
  preferExteriorEdges?: boolean;
  footprints?: FootprintPolygonsType | null;
};

export function snapToInnerEdge(
  position: { x: number; y: number },
  objectSize: number,
  targetSpace: GridSpaceDataType,
  options?: SnapToInnerEdgeOptions
): WallSnapPlacementType {
  const center = objectCenterFeet(position, objectSize);
  const sizeFt = objectSize / STRUCTURE_GRID_SIZE;
  const neighbors = gridSpaceSnapPolygons(
    (options?.allGridSpaces ?? []).filter(
      (space) => space.spaceId !== targetSpace.spaceId
    )
  );
  const snapped = snapInsidePolygon(
    center,
    sizeFt,
    gridSpaceSnapPolygon(targetSpace, options?.structure),
    {
      neighbors,
      preferExterior: options?.preferExteriorEdges,
      footprints: options?.footprints ?? null
    }
  );
  if (snapped) {
    return snapped;
  }
  return {
    ...position,
    center,
    edge: 'top',
    edgeIndex: 0,
    rotationDeg: 0
  };
}

export function snapToCenter(
  objectSize: number,
  targetSpace: GridSpaceDataType
): { x: number; y: number } {
  const interiorCenter = getSpaceInteriorCenter(targetSpace);

  return {
    x: (interiorCenter.x - objectSize / 2) / STRUCTURE_GRID_SIZE,
    y: (interiorCenter.y - objectSize / 2) / STRUCTURE_GRID_SIZE
  };
}

export function clampToSpace(
  position: { x: number; y: number },
  objectSize: number,
  targetSpace: GridSpaceDataType
): { x: number; y: number } {
  const objectPixelX = position.x * STRUCTURE_GRID_SIZE;
  const objectPixelY = position.y * STRUCTURE_GRID_SIZE;

  const minX = targetSpace.position.x;
  const minY = targetSpace.position.y;
  const maxX = targetSpace.position.x + targetSpace.position.width - objectSize;
  const maxY =
    targetSpace.position.y + targetSpace.position.height - objectSize;

  const clampedX = Math.min(Math.max(objectPixelX, minX), Math.max(maxX, minX));
  const clampedY = Math.min(Math.max(objectPixelY, minY), Math.max(maxY, minY));

  return {
    x: clampedX / STRUCTURE_GRID_SIZE,
    y: clampedY / STRUCTURE_GRID_SIZE
  };
}

type SnapPointType = {
  x: number;
  y: number;
  distance: number;
};

function getAdjacentPairCenters(
  zoneSpaces: GridSpaceDataType[],
  objectSize: number
): Array<{ x: number; y: number }> {
  const pairCenters: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < zoneSpaces.length; i += 1) {
    for (let j = i + 1; j < zoneSpaces.length; j += 1) {
      const space1 = zoneSpaces[i];
      const space2 = zoneSpaces[j];

      const space1Left = space1.position.x;
      const space1Right = space1.position.x + space1.position.width;
      const space1Top = space1.position.y;
      const space1Bottom = space1.position.y + space1.position.height;

      const space2Left = space2.position.x;
      const space2Right = space2.position.x + space2.position.width;
      const space2Top = space2.position.y;
      const space2Bottom = space2.position.y + space2.position.height;

      const horizontalOverlap =
        space1Left < space2Right && space1Right > space2Left;
      const verticalOverlap =
        space1Top < space2Bottom && space1Bottom > space2Top;

      const isAdjacentHorizontally =
        verticalOverlap &&
        (space1Right === space2Left || space2Right === space1Left);

      const isAdjacentVertically =
        horizontalOverlap &&
        (space1Bottom === space2Top || space2Bottom === space1Top);

      if (isAdjacentHorizontally || isAdjacentVertically) {
        let sharedEdgeStart: number;
        let sharedEdgeEnd: number;
        let fixedCoord: number;
        let isVerticalEdge: boolean;

        if (isAdjacentHorizontally) {
          sharedEdgeStart = Math.max(space1Top, space2Top);
          sharedEdgeEnd = Math.min(space1Bottom, space2Bottom);
          fixedCoord = space1Right === space2Left ? space1Right : space2Right;
          isVerticalEdge = true;
        } else {
          sharedEdgeStart = Math.max(space1Left, space2Left);
          sharedEdgeEnd = Math.min(space1Right, space2Right);
          fixedCoord = space1Bottom === space2Top ? space1Bottom : space2Bottom;
          isVerticalEdge = false;
        }

        const edgeCenter = (sharedEdgeStart + sharedEdgeEnd) / 2;

        if (isVerticalEdge) {
          pairCenters.push({
            x: (fixedCoord - objectSize / 2) / STRUCTURE_GRID_SIZE,
            y: (edgeCenter - objectSize / 2) / STRUCTURE_GRID_SIZE
          });
        } else {
          pairCenters.push({
            x: (edgeCenter - objectSize / 2) / STRUCTURE_GRID_SIZE,
            y: (fixedCoord - objectSize / 2) / STRUCTURE_GRID_SIZE
          });
        }
      }
    }
  }

  return pairCenters;
}

export function snapToZoneSpaces(
  position: { x: number; y: number },
  objectSize: number,
  zoneSpaces: GridSpaceDataType[]
): { x: number; y: number } {
  if (zoneSpaces.length === 0) {
    return position;
  }

  if (zoneSpaces.length === 1) {
    return snapToCenter(objectSize, zoneSpaces[0]);
  }

  const snapPoints: SnapPointType[] = [];
  const objectPixelX = position.x * STRUCTURE_GRID_SIZE;
  const objectPixelY = position.y * STRUCTURE_GRID_SIZE;
  const objectCenterX = objectPixelX + objectSize / 2;
  const objectCenterY = objectPixelY + objectSize / 2;

  for (const space of zoneSpaces) {
    const centerPos = snapToCenter(objectSize, space);
    const centerPixelX = centerPos.x * STRUCTURE_GRID_SIZE + objectSize / 2;
    const centerPixelY = centerPos.y * STRUCTURE_GRID_SIZE + objectSize / 2;

    const distance = Math.sqrt(
      (objectCenterX - centerPixelX) ** 2 + (objectCenterY - centerPixelY) ** 2
    );

    snapPoints.push({
      x: centerPos.x,
      y: centerPos.y,
      distance
    });
  }

  const pairCenters = getAdjacentPairCenters(zoneSpaces, objectSize);
  for (const pairCenter of pairCenters) {
    const pairPixelX = pairCenter.x * STRUCTURE_GRID_SIZE + objectSize / 2;
    const pairPixelY = pairCenter.y * STRUCTURE_GRID_SIZE + objectSize / 2;

    const distance = Math.sqrt(
      (objectCenterX - pairPixelX) ** 2 + (objectCenterY - pairPixelY) ** 2
    );

    snapPoints.push({
      x: pairCenter.x,
      y: pairCenter.y,
      distance
    });
  }

  snapPoints.sort((a, b) => a.distance - b.distance);

  if (snapPoints.length > 0) {
    return { x: snapPoints[0].x, y: snapPoints[0].y };
  }

  return snapToCenter(objectSize, zoneSpaces[0]);
}

export function getContainingSpace(
  position: { x: number; y: number },
  objectSize: number,
  gridSpaces: GridSpaceDataType[]
): GridSpaceDataType | null {
  const objectPixelX = position.x * STRUCTURE_GRID_SIZE;
  const objectPixelY = position.y * STRUCTURE_GRID_SIZE;
  const objectCenterX = objectPixelX + objectSize / 2;
  const objectCenterY = objectPixelY + objectSize / 2;
  const objectCenter = { x: objectCenterX, y: objectCenterY };

  const containing = gridSpaces.find((space) => {
    const spaceLeft = space.position.x;
    const spaceRight = space.position.x + space.position.width;
    const spaceTop = space.position.y;
    const spaceBottom = space.position.y + space.position.height;

    const insideBbox =
      objectCenterX >= spaceLeft &&
      objectCenterX <= spaceRight &&
      objectCenterY >= spaceTop &&
      objectCenterY <= spaceBottom;

    if (!insideBbox) return false;

    if (isPolygonShapedSpace(space)) {
      const absoluteVertices = getSpaceAbsoluteVertices(space);
      if (
        absoluteVertices.length >= 3 &&
        !isPointInPolygon(objectCenter, absoluteVertices)
      ) {
        return false;
      }
    }

    return true;
  });

  return containing ?? null;
}

export function findNearestExteriorPointForLineset(
  position: { x: number; y: number },
  gridSpaces: GridSpaceDataType[]
): { x: number; y: number } | null {
  if (gridSpaces.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const space of gridSpaces) {
    const left = space.position.x / STRUCTURE_GRID_SIZE;
    const right =
      (space.position.x + space.position.width) / STRUCTURE_GRID_SIZE;
    const top = space.position.y / STRUCTURE_GRID_SIZE;
    const bottom =
      (space.position.y + space.position.height) / STRUCTURE_GRID_SIZE;

    minX = Math.min(minX, left);
    maxX = Math.max(maxX, right);
    minY = Math.min(minY, top);
    maxY = Math.max(maxY, bottom);
  }

  const distToLeft = Math.abs(position.x - minX);
  const distToRight = Math.abs(position.x - maxX);
  const distToTop = Math.abs(position.y - minY);
  const distToBottom = Math.abs(position.y - maxY);

  const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);

  if (minDist === distToLeft) {
    return { x: minX - 1, y: position.y };
  }
  if (minDist === distToRight) {
    return { x: maxX + 1, y: position.y };
  }
  if (minDist === distToTop) {
    return { x: position.x, y: minY - 1 };
  }
  return { x: position.x, y: maxY + 1 };
}

export function getSelectionBoundingBox(
  selectedSpaceIds: Set<string>,
  gridSpaces: GridSpaceDataType[]
): PositionAndDimensionsType | null {
  if (selectedSpaceIds.size < 2) return null;

  const selectedSpaces = gridSpaces.filter((space) =>
    selectedSpaceIds.has(space.spaceId)
  );

  if (selectedSpaces.length < 2) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const space of selectedSpaces) {
    minX = Math.min(minX, space.position.x);
    minY = Math.min(minY, space.position.y);
    maxX = Math.max(maxX, space.position.x + space.position.width);
    maxY = Math.max(maxY, space.position.y + space.position.height);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

export type ProportionalScaleHandle = 'nw' | 'ne' | 'sw' | 'se';

export type ProportionalScaleParams = {
  selectedSpaceIds: Set<string>;
  handle: ProportionalScaleHandle;
  deltaX: number;
  deltaY: number;
  initialBoundingBox: PositionAndDimensionsType;
  initialSpacePositions: Map<string, PositionAndDimensionsType>;
  uniform?: boolean;
};

function checkPixelRectsOverlap(
  rect1: PositionAndDimensionsType,
  rect2: PositionAndDimensionsType
): boolean {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
}

/* eslint-disable no-param-reassign */
function resolveOverlapBetweenSpaces(
  pos1: PositionAndDimensionsType,
  pos2: PositionAndDimensionsType,
  initial1: PositionAndDimensionsType,
  initial2: PositionAndDimensionsType
): void {
  const wasRightOf = initial2.x >= initial1.x + initial1.width;
  const wasLeftOf = initial1.x >= initial2.x + initial2.width;
  const wasBelow = initial2.y >= initial1.y + initial1.height;
  const wasAbove = initial1.y >= initial2.y + initial2.height;
  const minSize = MIN_SPACE_DIM_FEET * STRUCTURE_GRID_SIZE;

  if (wasRightOf) {
    pos2.x = pos1.x + pos1.width;
    pos2.width = Math.max(minSize, pos2.width);
  } else if (wasLeftOf) {
    pos1.x = pos2.x + pos2.width;
    pos1.width = Math.max(minSize, pos1.width);
  } else if (wasBelow) {
    pos2.y = pos1.y + pos1.height;
    pos2.height = Math.max(minSize, pos2.height);
  } else if (wasAbove) {
    pos1.y = pos2.y + pos2.height;
    pos1.height = Math.max(minSize, pos1.height);
  }
}
/* eslint-enable no-param-reassign */

function resolveAllOverlaps(
  pixelPositions: Map<string, PositionAndDimensionsType>,
  initialSpacePositions: Map<string, PositionAndDimensionsType>
): void {
  const spaceIds = Array.from(pixelPositions.keys());
  for (let i = 0; i < spaceIds.length; i += 1) {
    for (let j = i + 1; j < spaceIds.length; j += 1) {
      const pos1 = pixelPositions.get(spaceIds[i])!;
      const pos2 = pixelPositions.get(spaceIds[j])!;

      if (checkPixelRectsOverlap(pos1, pos2)) {
        const initial1 = initialSpacePositions.get(spaceIds[i])!;
        const initial2 = initialSpacePositions.get(spaceIds[j])!;
        resolveOverlapBetweenSpaces(pos1, pos2, initial1, initial2);
      }
    }
  }
}

export function calculateProportionalScalePositions({
  selectedSpaceIds,
  handle,
  deltaX,
  deltaY,
  initialBoundingBox,
  initialSpacePositions,
  uniform = false
}: ProportionalScaleParams): Map<string, PositionAndDimensionsType> {
  const result = new Map<string, PositionAndDimensionsType>();

  let anchorX: number;
  let anchorY: number;

  switch (handle) {
    case 'nw':
      anchorX = initialBoundingBox.x + initialBoundingBox.width;
      anchorY = initialBoundingBox.y + initialBoundingBox.height;
      break;
    case 'ne':
      anchorX = initialBoundingBox.x;
      anchorY = initialBoundingBox.y + initialBoundingBox.height;
      break;
    case 'sw':
      anchorX = initialBoundingBox.x + initialBoundingBox.width;
      anchorY = initialBoundingBox.y;
      break;
    case 'se':
    default:
      anchorX = initialBoundingBox.x;
      anchorY = initialBoundingBox.y;
      break;
  }

  let newWidth = initialBoundingBox.width;
  let newHeight = initialBoundingBox.height;

  switch (handle) {
    case 'nw':
      newWidth = initialBoundingBox.width - deltaX;
      newHeight = initialBoundingBox.height - deltaY;
      break;
    case 'ne':
      newWidth = initialBoundingBox.width + deltaX;
      newHeight = initialBoundingBox.height - deltaY;
      break;
    case 'sw':
      newWidth = initialBoundingBox.width - deltaX;
      newHeight = initialBoundingBox.height + deltaY;
      break;
    case 'se':
      newWidth = initialBoundingBox.width + deltaX;
      newHeight = initialBoundingBox.height + deltaY;
      break;
    default:
      break;
  }

  const minSize = MIN_SPACE_DIM_FEET * STRUCTURE_GRID_SIZE;
  newWidth = Math.max(minSize, newWidth);
  newHeight = Math.max(minSize, newHeight);

  let scaleX = newWidth / initialBoundingBox.width;
  let scaleY = newHeight / initialBoundingBox.height;

  if (uniform) {
    const uniformScale =
      Math.abs(scaleX - 1) > Math.abs(scaleY - 1) ? scaleX : scaleY;
    scaleX = uniformScale;
    scaleY = uniformScale;
  }

  const pixelPositions = new Map<string, PositionAndDimensionsType>();

  for (const spaceId of selectedSpaceIds) {
    const initialPos = initialSpacePositions.get(spaceId);
    if (initialPos) {
      const relX = initialPos.x - anchorX;
      const relY = initialPos.y - anchorY;
      const relRight = initialPos.x + initialPos.width - anchorX;
      const relBottom = initialPos.y + initialPos.height - anchorY;

      const newRelX = relX * scaleX;
      const newRelY = relY * scaleY;
      const newRelRight = relRight * scaleX;
      const newRelBottom = relBottom * scaleY;

      let newX = anchorX + newRelX;
      let newY = anchorY + newRelY;
      let newSpaceWidth = newRelRight - newRelX;
      let newSpaceHeight = newRelBottom - newRelY;

      newX = Math.round(newX / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE;
      newY = Math.round(newY / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE;
      newSpaceWidth =
        Math.round(newSpaceWidth / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE;
      newSpaceHeight =
        Math.round(newSpaceHeight / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE;

      newSpaceWidth = Math.max(
        MIN_SPACE_DIM_FEET * STRUCTURE_GRID_SIZE,
        newSpaceWidth
      );
      newSpaceHeight = Math.max(
        MIN_SPACE_DIM_FEET * STRUCTURE_GRID_SIZE,
        newSpaceHeight
      );

      pixelPositions.set(spaceId, {
        x: newX,
        y: newY,
        width: newSpaceWidth,
        height: newSpaceHeight
      });
    }
  }

  resolveAllOverlaps(pixelPositions, initialSpacePositions);

  for (const [spaceId, pos] of pixelPositions) {
    result.set(spaceId, {
      x: pos.x / STRUCTURE_GRID_SIZE,
      y: pos.y / STRUCTURE_GRID_SIZE,
      width: pos.width / STRUCTURE_GRID_SIZE,
      height: pos.height / STRUCTURE_GRID_SIZE
    });
  }

  return result;
}

export function areSpacesInViewport(
  spaces: GridSpaceDataType[],
  containerWidth: number,
  containerHeight: number,
  zoom: number,
  panOffset: { x: number; y: number }
): boolean {
  if (spaces.length === 0) return false;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const space of spaces) {
    minX = Math.min(minX, space.position.x);
    minY = Math.min(minY, space.position.y);
    maxX = Math.max(maxX, space.position.x + space.position.width);
    maxY = Math.max(maxY, space.position.y + space.position.height);
  }

  const visMinX = -panOffset.x / zoom;
  const visMinY = -panOffset.y / zoom;
  const visMaxX = (containerWidth - panOffset.x) / zoom;
  const visMaxY = (containerHeight - panOffset.y) / zoom;

  return (
    minX >= visMinX && minY >= visMinY && maxX <= visMaxX && maxY <= visMaxY
  );
}

export const isSmallSpace = (widthPx: number, heightPx: number): boolean => {
  const widthFt = widthPx / 10;
  const heightFt = heightPx / 10;
  return widthFt < 8 || heightFt < 6;
};
